import React, { FC, useEffect } from "react";
import * as d3 from "d3";

import Histogram from "./histogram";
import { ResizeComponent, SizeProps } from "containers/hooks/ResizeComponent";
import { useDatapoints, useDatapointsSelectionFiltered, useUnfilteredDatapoints } from "containers/hooks/useData";
import { useAppSelector } from "index";
import { ApplicationState } from "@store/index";
import { TimelineRangeType } from "@store/filters/types";
import { DataPoint } from "fileParsing/types";
import { getMinMax } from "@utils/date";

export type TimelineSelectorSettings = {
  width: number;
  height: number;
  padding: { left: number; top: number; right: number; bottom: number };
  brushWidth: number;
  bins: number;
  binsMaxSize: d3.TimeInterval | null;
  transitionDuration: number;
  xAxisTicks?: number;
};

export const VisualizationSettingsDefaults: TimelineSelectorSettings = {
  width: 1024,
  height: 140,
  padding: { left: 40, top: 10, right: 30, bottom: 20 },
  brushWidth: 1024,
  bins: 40,
  binsMaxSize: d3.timeMonth.every(3),
  transitionDuration: 300,
};

// Component-specific props.
type ComponentProps = {
  settings: Partial<TimelineSelectorSettings>;
  onSelected?: (selection: TimelineRangeType) => void;
};

const TimelineSelector: FC<ComponentProps & SizeProps> = (props) => {
  const settings = getSettings();

  const fullData: DataPoint[] = useDatapoints();
  const data = useDatapointsSelectionFiltered();
  const unfilteredData = useUnfilteredDatapoints();

  const selection = useAppSelector((state: ApplicationState) => state.filters.selection);

  const [fullDataRange, setFullDataRange] = React.useState<TimelineRangeType>(getMinMax(unfilteredData));

  useEffect(() => {
    const newRange = getMinMax(unfilteredData);
    const oldRange = fullDataRange;

    setFullDataRange(newRange);

    if (fullData.length === 0) return;

    const newSelection = selection;

    // if the selection and newRange are well-defined
    if (selection && selection[0] && selection[1] && newRange[0] && newRange[1]) {
      // If the left selection is out of bounds
      if (selection[0] < newRange[0] || selection[0] > newRange[1]) newSelection[0] = newRange[0];

      // If the right selection is out of bounds
      if (selection[1] < newRange[0] || selection[1] > newRange[1]) newSelection[1] = newRange[1];

      // If fullDataRange (aka old range) is well-defined
      if (oldRange[0] && oldRange[1]) {
        // If the left old selection was bounded to the left available range it was a default selection. Keep this.
        if (selection[0]?.getTime() === oldRange[0]?.getTime()) newSelection[0] = newRange[0];

        // If the right old selection was bounded to the right available range it was a default selection. Keep this.
        if (selection[1]?.getTime() === oldRange[1]?.getTime()) newSelection[1] = newRange[1];
      }
      if (newSelection?.[0] && newSelection?.[1]) brushed([newSelection[0], newSelection[1]]);
    }
  }, [unfilteredData.length]);

  function getSettings(): TimelineSelectorSettings {
    return {
      ...VisualizationSettingsDefaults,
      ...props.settings,
    };
  }

  function brushed(newSelection: [Date, Date] | null) {
    // apply selection
    if (newSelection && !isNaN(newSelection[0].getTime()) && !isNaN(newSelection[1].getTime())) {
      props.onSelected?.(newSelection);
    } else {
      props.onSelected?.(getMinMax(data));
    }
  }

  return (
    <>
      <Histogram
        data={fullData}
        settings={{
          width: props.size.width,
          height: 60,
          xAxisTicks: settings.xAxisTicks,
          yAxesHidden: true,
          enableBrushing: true,
          minMaxBound: fullDataRange,
          selectionBounds: selection,
          bins: settings.bins,
          padding: settings.padding,
        }}
        onBrush={(selection) => brushed(selection)}
      />
    </>
  );
};

export default ResizeComponent(TimelineSelector);
